/**
 * Azure AD (Entra ID) Configuration
 * Same tenant as Investor Portal, separate App Registration.
 *
 * TODO: Create a new App Registration in Azure Portal:
 *   1. Azure Portal → App Registrations → New Registration
 *   2. Name: "Aviator Daily Dashboard"
 *   3. Supported account types: AzureADandPersonalMicrosoftAccount
 *   4. Redirect URI: https://<your-swa-url> (add after deployment)
 *   5. Copy the Application (client) ID below
 */

import { PublicClientApplication } from '@azure/msal-browser';

const TENANT_ID = '60895329-770e-4a73-ad30-08f842948516';  // Aviator Energy tenant
const CLIENT_ID = 'a0256ce7-1b5c-4995-91e2-fe54ec6570b1';  // App registration (verified live against tenant OIDC discovery 2026-09-08)

export const msalConfig = {
    auth: {
        clientId: CLIENT_ID,
        authority: `https://login.microsoftonline.com/${TENANT_ID}`,
        redirectUri: window.location.origin,
        postLogoutRedirectUri: window.location.origin,
    },
    cache: {
        cacheLocation: 'sessionStorage',
        storeAuthStateInCookie: false,
    },
};

export const loginRequest = {
    scopes: ['openid', 'profile', 'email'],
};

export const apiConfig = {
    baseUrl: import.meta.env.VITE_API_URL || '/api',
};

// Single MSAL instance for the whole app. Exported so non-React code (the global
// fetch wrapper in main.jsx) can acquire tokens without a hook.
export const msalInstance = new PublicClientApplication(msalConfig);

// True when a JWT is expired or expires within the next 2 minutes (the API
// rejects expired tokens, so a token about to expire mid-request is as bad as
// an expired one). Unparseable tokens count as expiring so we refresh them.
function idTokenExpiring(token) {
    try {
        const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
        return !payload.exp || (payload.exp * 1000) < Date.now() + 2 * 60 * 1000;
    } catch {
        return true;
    }
}

// Acquire the current user's Entra ID token for the API. Returns the raw JWT, or
// null if there is no signed-in account. The API validates this token's
// signature, issuer, audience, EXPIRY and email domain - see
// api/shared/require-aviator.js.
//
// MSAL v3 gotcha (the cause of the recurring "Error loading data" on every tab):
// acquireTokenSilent serves from cache keyed off the ACCESS token's lifetime
// (60-90 min), but the ID token only lives 60 min. In the gap, MSAL returns a
// cached, already-expired ID token; the API 401s it and the whole dashboard
// dies until the access token also expires. So we check the ID token's own exp
// and force a refresh when it is stale.
//
// If silent refresh fails outright the session is gone (refresh token expired /
// revoked). Returning null forever just leaves a dead dashboard, so we send the
// user through an interactive redirect to sign back in - guarded by a
// sessionStorage flag so a failing login can never redirect-loop.
export async function getApiToken() {
    const account = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];
    if (!account) return null;
    try {
        let result = await msalInstance.acquireTokenSilent({ ...loginRequest, account });
        if (!result.idToken || idTokenExpiring(result.idToken)) {
            result = await msalInstance.acquireTokenSilent({ ...loginRequest, account, forceRefresh: true });
        }
        sessionStorage.removeItem('dd_auth_redirected');
        return result.idToken || null;
    } catch {
        if (!sessionStorage.getItem('dd_auth_redirected')) {
            sessionStorage.setItem('dd_auth_redirected', '1');
            try { await msalInstance.acquireTokenRedirect({ ...loginRequest, account }); } catch {}
        }
        return null;
    }
}
